import React, { useState } from 'react';
import '@sendbird/uikit-react/dist/index.css';
import { AlertCircle, Plus, Users, Crown } from 'lucide-react';
// @ts-ignore - SendbirdProvider may have type issues
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
// @ts-ignore - Component imports may have type issues  
import { GroupChannelList } from '@sendbird/uikit-react/GroupChannelList';
// @ts-ignore - Component imports may have type issues
import { GroupChannel } from '@sendbird/uikit-react/GroupChannel';
// @ts-ignore - Component imports may have type issues
import MessageSearch from '@sendbird/uikit-react/MessageSearch';
// @ts-ignore - Component imports may have type issues
import ChannelSettings from '@sendbird/uikit-react/ChannelSettings';
// @ts-ignore - Component imports may have type issues
import Thread from '@sendbird/uikit-react/Thread';
import type { GroupChannel as GroupChannelType } from '@sendbird/chat/groupChannel';
import { CreateChannelModal } from './CreateChannelModal';
import type { GroupDetail } from '../../../../types/groupDetail';

/**
 * Configuration Error Component
 * Displays when Sendbird is not properly configured
 */
const SendbirdConfigError: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center h-[600px] bg-white rounded-lg shadow-sm">
    <div className="text-center max-w-md p-6">
      <AlertCircle className="h-12 w-12 mx-auto mb-4 text-orange-500" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Messaging Not Configured</h3>
      <p className="text-gray-600 mb-4">{message}</p>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
        <p className="text-sm font-medium text-gray-700 mb-2">Required Configuration:</p>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Set <code className="bg-gray-200 px-1 rounded">VITE_SENDBIRD_APP_ID</code> in your .env file</li>
        </ul>
        <p className="text-sm text-gray-500 mt-3">
          See <strong>SENDBIRD_INTEGRATION.md</strong> for setup instructions.
        </p>
      </div>
    </div>
  </div>
);

/**
 * SendbirdMessaging Component
 * Implements Sendbird UIKit with separate channel list and channel views
 * 
 * This component provides a two-pane interface with a channel list on the left
 * and the selected channel conversation on the right. On mobile, it switches
 * between list and channel views.
 * 
 * Automatically creates two default channels for each group:
 * - All Participants: {subdomain}-{groupId}-all
 * - Group Leaders: {subdomain}-{groupId}-leaders
 *
 * @requires VITE_SENDBIRD_APP_ID environment variable
 */
interface SendbirdMessagingProps {
  /** User ID (CHMS individual ID) for the current user */
  userId: string;
  /** Avatar URL for the current user (group leader) */
  profileUrl?: string;
  /** Nickname for the current user (group leader) */
  nickname?: string;
  /** Group details for auto-creating default channels */
  groupDetail: GroupDetail;
}

export const SendbirdMessaging: React.FC<SendbirdMessagingProps> = ({ 
  userId, 
  profileUrl, 
  nickname,
  groupDetail,
}) => {
  // State to track the currently selected channel
  const [currentChannelUrl, setCurrentChannelUrl] = useState<string>('');
  const [showChannelList, setShowChannelList] = useState(true);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [showChannelSettings, setShowChannelSettings] = useState(false);
  const [threadTargetMessage, setThreadTargetMessage] = useState<any>(null);
  
  // Validate required environment variables
  const appId = import.meta.env.VITE_SENDBIRD_APP_ID;

  // Get group ID and subdomain for filtering and custom channel types
  const groupId = groupDetail?.id || '';
  const subdomain = 'beta'; // TODO: Make this configurable if needed

  // Determine if the current channel is a default channel
  const isDefaultChannel = currentChannelUrl && (
    currentChannelUrl === `${subdomain}-${groupId}-all` ||
    currentChannelUrl === `${subdomain}-${groupId}-leaders`
  );

  // Show configuration error if required env vars are missing
  if (!appId) {
    return (
      <SendbirdConfigError
        message="Sendbird App ID is not configured. Please set the VITE_SENDBIRD_APP_ID environment variable."
      />
    );
  }

  if (!userId) {
    return (
      <SendbirdConfigError
        message="User ID is required for messaging functionality."
      />
    );
  }

  // Handle channel selection from the list
  const handleChannelSelect = (channel: GroupChannelType | null) => {
    if (channel) {
      setCurrentChannelUrl(channel.url);
      setShowChannelList(false);
    }
  };

  // Handle back button to return to channel list
  const handleBackToList = () => {
    setShowChannelList(true);
  };

  // Custom header for channel list
  const renderChannelListHeader = () => (
    <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 bg-white">
      <h2 className="text-lg font-semibold text-gray-900">Channels</h2>
      <button
        onClick={() => setShowCreateChannel(true)}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Create channel"
      >
        <Plus className="w-5 h-5 text-gray-700" />
      </button>
    </div>
  );

  // Custom channel preview to show cleaner names
  const renderChannelPreview = ({ channel, isSelected }: { channel: GroupChannelType; isSelected?: boolean }) => {
    // Extract friendly name from custom type
    let displayName = channel.name;
    let avatarBgColor = 'bg-gray-100';
    let iconColor = 'text-gray-600';
    let icon = <Users className="w-6 h-6 text-gray-600" />;
    let channelIsDefault = false;
    
    if (channel.customType === `${subdomain}-${groupId}-all-participants`) {
      displayName = 'All Participants';
      avatarBgColor = 'bg-blue-100';
      iconColor = 'text-blue-600';
      icon = <Users className={`w-6 h-6 ${iconColor}`} />;
      channelIsDefault = true;
    } else if (channel.customType === `${subdomain}-${groupId}-leaders`) {
      displayName = 'Group Leaders';
      avatarBgColor = 'bg-purple-100';
      iconColor = 'text-purple-600';
      icon = <Crown className={`w-6 h-6 ${iconColor}`} />;
      channelIsDefault = true;
    } else if (channel.customType === `${subdomain}-${groupId}-custom`) {
      // Custom channel - use the channel name as-is
      avatarBgColor = 'bg-green-100';
      iconColor = 'text-green-600';
      icon = <Users className={`w-6 h-6 ${iconColor}`} />;
    }

    // Format timestamp for last message or channel activity
    const formatTimestamp = (timestamp: number) => {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      // Show date for older messages
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const lastActivityTime = channel.lastMessage?.createdAt || channel.createdAt || Date.now();

    return (
      <div className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
        isSelected 
          ? 'bg-blue-50 border-l-4 border-blue-600' 
          : 'hover:bg-gray-50 border-l-4 border-transparent'
      }`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center overflow-hidden ${avatarBgColor}`}>
          {!channelIsDefault && channel.coverUrl ? (
            <img 
              src={channel.coverUrl} 
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            icon
          )}
        </div>

        {/* Channel info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className={`text-sm font-semibold truncate ${
              isSelected ? 'text-blue-900' : 'text-gray-900'
            }`}>
              {displayName}
            </h3>
            <span className={`text-xs ml-2 flex-shrink-0 ${
              isSelected ? 'text-blue-600' : 'text-gray-500'
            }`}>
              {formatTimestamp(lastActivityTime)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-1">
            {channel.lastMessage ? (
              <p className={`text-sm truncate flex-1 ${
                isSelected ? 'text-blue-700' : 'text-gray-500'
              }`}>
                {/* @ts-ignore */}
                {channel.lastMessage.message || channel.lastMessage.name || 'New message'}
              </p>
            ) : (
              <p className={`text-sm truncate flex-1 italic ${
                isSelected ? 'text-blue-600' : 'text-gray-400'
              }`}>
                No messages yet
              </p>
            )}
            <div className="ml-2 flex-shrink-0 flex items-center gap-2">
              <div className={`flex items-center gap-1 text-xs ${
                isSelected ? 'text-blue-600' : 'text-gray-500'
              }`}>
                <Users className="w-3 h-3" />
                <span>{channel.memberCount || 0}</span>
              </div>
              {channel.unreadMessageCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
                  {channel.unreadMessageCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    // @ts-ignore - SendbirdProvider children prop typing
    <SendbirdProvider
      appId={appId}
      userId={userId}
      profileUrl={profileUrl}
      nickname={nickname}
      breakpoint="1024px"
      disableUserProfile={true}
      onStartDirectMessage={() => {
        // Disable direct messaging from user profiles
        return;
      }}
      uikitOptions={{
        common: {
          enableUsingDefaultUserProfile: false,
        },
        groupChannel: {
          replyType: 'thread',
        },
      }}
    >
      <style>{`
        /* Hide leave channel button only for default channels */
        ${isDefaultChannel ? `
          .sendbird-channel-settings__panel-item__leave-channel {
            display: none !important;
          }
        ` : ''}
      `}</style>
      <div className="sendbird-messaging h-[600px] bg-white rounded-lg shadow-sm overflow-hidden flex relative">
        {/* Create Channel Modal */}
        {showCreateChannel && (
          <CreateChannelModal
            groupDetail={groupDetail}
            subdomain={subdomain}
            onClose={() => setShowCreateChannel(false)}
            onChannelCreated={(channelUrl) => {
              setShowCreateChannel(false);
              setCurrentChannelUrl(channelUrl);
              setShowChannelList(false);
            }}
          />
        )}

        {/* Channel List - Always visible on desktop, hidden on mobile when channel is selected */}
        <div className={`${showChannelList ? 'w-full lg:w-80' : 'hidden lg:block lg:w-80'} border-r border-gray-200`}>
              {/* @ts-ignore - Component prop typing */}
              <GroupChannelList
                onChannelSelect={handleChannelSelect}
                onChannelCreated={(channel: GroupChannelType) => {
                  setShowCreateChannel(false);
                  if (channel) {
                    setCurrentChannelUrl(channel.url);
                    setShowChannelList(false);
                  }
                }}
                selectedChannelUrl={currentChannelUrl}
                disableAutoSelect={false}
                renderHeader={renderChannelListHeader}
                renderChannelPreview={renderChannelPreview}
                channelListQueryParams={{
                  customTypesFilter: [
                    `${subdomain}-${groupId}-all-participants`,
                    `${subdomain}-${groupId}-leaders`,
                    `${subdomain}-${groupId}-custom`
                  ],
                  includeEmpty: true,
                }}
              />
            </div>

            {/* Channel Messages - Shows when a channel is selected */}
            {currentChannelUrl && (
              <div className={`${showChannelList ? 'hidden lg:flex' : 'flex'} flex-1 w-full [&>*]:!border-t-0 [&>*]:!border-b-0 [&>*]:!border-r-0 [&>*]:w-full`}>
                {/* @ts-ignore - Component prop typing */}
                <GroupChannel
                  channelUrl={currentChannelUrl}
                  onBackClick={handleBackToList}
                  onSearchClick={() => setShowMessageSearch(true)}
                  showSearchIcon={true}
                  onChatHeaderActionClick={() => setShowChannelSettings(true)}
                  onReplyInThreadClick={(props: any) => {
                    setThreadTargetMessage(props.message);
                  }}
                  replyType="THREAD"
                />
              </div>
            )}

            {/* Empty state when no channel is selected */}
            {!currentChannelUrl && (
              <div className={`${showChannelList ? 'hidden lg:flex' : 'hidden'} flex-1 items-center justify-center bg-gray-50`}>
                <div className="text-center text-gray-500">
                </div>
              </div>
            )}

            {/* Message Search Drawer - Slides in from the right */}
            {showMessageSearch && currentChannelUrl && (
              <>
                {/* Drawer */}
                <div className="absolute top-0 right-0 bottom-0 w-full lg:w-80 bg-white shadow-xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out">
                  {/* @ts-ignore - Component prop typing */}
                  <MessageSearch
                    channelUrl={currentChannelUrl}
                    onResultClick={() => {
                      setShowMessageSearch(false);
                    }}
                    onCloseClick={() => setShowMessageSearch(false)}
                  />
                </div>
              </>
            )}

            {/* Channel Settings Drawer - Slides in from the right */}
            {showChannelSettings && currentChannelUrl && (
              <>
                {/* Drawer */}
                <div className="absolute top-0 right-0 bottom-0 w-full lg:w-80 bg-white shadow-xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out">
                  {/* @ts-ignore - Component prop typing */}
                  <ChannelSettings
                    channelUrl={currentChannelUrl}
                    onCloseClick={() => setShowChannelSettings(false)}
                  />
                </div>
              </>
            )}

            {/* Thread Drawer - Slides in from the right */}
            {threadTargetMessage && currentChannelUrl && (
              <>
                <style>{`
                  .sendbird-thread-ui,
                  .sendbird-thread-ui__header,
                  .sendbird-parent-message-info {
                    border-bottom: 0px !important;
                  }
                  .sendbird-thread-ui__header {
                    border-top: 0px !important;
                  }
                  @media (max-width: 1023px) {
                    .sendbird-thread-ui,
                    .sendbird-thread-ui__header {
                      width: 100% !important;
                      max-width: 100% !important;
                    }
                  }
                `}</style>
                {/* Drawer */}
                <div className="absolute top-0 right-0 bottom-0 w-full lg:w-80 bg-white shadow-xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out [&>*]:h-full">
                  {/* @ts-ignore - Component prop typing */}
                  <Thread
                    channelUrl={currentChannelUrl}
                    message={threadTargetMessage}
                    onHeaderActionClick={() => {
                      setThreadTargetMessage(null);
                    }}
                    onMoveToParentMessage={() => {
                      setThreadTargetMessage(null);
                    }}
                  />
                </div>
              </>
            )}
      </div>
    </SendbirdProvider>
  );
};
